/**
 * 「人材像が求める水準」列の充足/不足判定 (純粋関数)。
 * docs/design/v05-skill-registration-table-redesign-detail.md §8-2 項目2。
 *
 * 判定は sort_order の比較のみ (current_level_sort_order >= required_level_sort_order)。
 * requiredSkillLevelId は対象スキルの scale_id に紐づく水準からしか選ばれない (useSkillMatrixRows §4-7)
 * ため、異なる尺度の水準同士を比較するケースはデータ構造上発生しない。
 */

export type SkillLevelForComparison = { skill_level_id: string; sort_order: number };

export type SkillLevelFulfillment = 'fulfilled' | 'unfulfilled' | 'unknown';

/** 求められる水準が無い (unknown) 行はバッジ非表示・サマリー集計対象外として扱う。 */
export function deriveSkillLevelFulfillment(
  currentSkillLevelId: string | null,
  requiredSkillLevelId: string | null,
  skillLevelsMaster: SkillLevelForComparison[],
): SkillLevelFulfillment {
  if (!requiredSkillLevelId) return 'unknown';
  if (!currentSkillLevelId) return 'unfulfilled';
  const sortOrderById = new Map(skillLevelsMaster.map((l) => [l.skill_level_id, l.sort_order]));
  const currentSortOrder = sortOrderById.get(currentSkillLevelId);
  const requiredSortOrder = sortOrderById.get(requiredSkillLevelId);
  if (currentSortOrder === undefined || requiredSortOrder === undefined) return 'unknown';
  return currentSortOrder >= requiredSortOrder ? 'fulfilled' : 'unfulfilled';
}

/** 表上部のサマリー行「選択中の人材像が求めるスキル ○個中○個充足」用の件数集計。 */
export function summarizeSkillLevelFulfillment(
  rows: { currentSkillLevelId: string | null; requiredSkillLevelId: string | null }[],
  skillLevelsMaster: SkillLevelForComparison[],
): { total: number; fulfilled: number } {
  let total = 0;
  let fulfilled = 0;
  for (const row of rows) {
    if (!row.requiredSkillLevelId) continue;
    total += 1;
    if (deriveSkillLevelFulfillment(row.currentSkillLevelId, row.requiredSkillLevelId, skillLevelsMaster) === 'fulfilled') {
      fulfilled += 1;
    }
  }
  return { total, fulfilled };
}
